import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { expendituresService } from "../services/expendituresService";
import s3Service from "../services/s3Service";
import styles from "../assets/expenseAccountEdit.module.css";

const ExpenseAccountEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [propertySearchResults, setPropertySearchResults] = useState([]);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  
  // Date picker states
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const dueDatePickerRef = useRef(null);
  const [dueDatePickerMonth, setDueDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [dueDatePickerYear, setDueDatePickerYear] = useState(new Date().getFullYear());
  const [expenditure, setExpenditure] = useState({
    id: "",
    property_id: "",
    property_name: "",
    type_id: "",
    bank_account_id: "",
    creditor: "",
    ticket_id: "",
    due_date: "",
    amount: "",
    invoice_number: "",
    purchase_order_number: "",
    description: "",
    file: null,
    confirmation_file: null,
    fileKey: null,
    confirmationFileKey: null,
    status: 0,
    amount_paid: 0
  });

  // Dropdown options
  const [types, setTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [properties, setProperties] = useState([]);

  // File states
  const [existingFile, setExistingFile] = useState(null);
  const [existingConfirmationFile, setExistingConfirmationFile] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [newConfirmationFile, setNewConfirmationFile] = useState(null);

  // Memoized loadData function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load from API - Laravel edit method returns { expense, formData }
      try {
        console.log("[EXPENSE_ACCOUNT_EDIT] Loading expense data for ID:", id);
        
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Request timeout - API call took too long")), 30000)
        );
        
        const apiPromise = expendituresService.getExpenseAccountItem(id);
        const expenditureResponse = await Promise.race([apiPromise, timeoutPromise]);
        
        console.log("[EXPENSE_ACCOUNT_EDIT] API Response received:", expenditureResponse);
        
        // Service returns response.data, so expenditureResponse is { success: true, data: { expense, formData } }
        if (!expenditureResponse) {
          throw new Error("No response from server");
        }
        
        if (!expenditureResponse.success) {
          throw new Error(expenditureResponse?.message || "Failed to load expense data");
        }
        
        // Backend returns: { success: true, data: { expense: {...}, formData: { properties, types, categories, bankAccounts } } }
        const expenseData = expenditureResponse.data?.expense || expenditureResponse.data || {};
        const formData = expenditureResponse.data?.formData || {};
        
        console.log("[EXPENSE_ACCOUNT_EDIT] Extracted expense data:", expenseData);
        console.log("[EXPENSE_ACCOUNT_EDIT] Extracted form data:", formData);
        
        // Validate that we have at least an expense ID
        if (!expenseData.id && !expenseData.expense_id) {
          console.warn("[EXPENSE_ACCOUNT_EDIT] Warning: Expense data missing ID:", expenseData);
        }
        
        // Set expense data
        setExpenditure(expenseData);
        setExistingFile(expenseData.file);
        setExistingConfirmationFile(expenseData.confirmation_file);
        
        // Initialize date picker month/year from existing due_date if available
        if (expenseData.due_date) {
          // Parse DD/MM/YYYY format
          const dateParts = expenseData.due_date.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10);
            const year = parseInt(dateParts[2], 10);
            if (!isNaN(month) && !isNaN(year)) {
              setDueDatePickerMonth(month);
              setDueDatePickerYear(year);
            }
          }
        }
        
        // Set form data from backend response (matches Laravel flow)
        setTypes(formData.types || []);
        setBankAccounts(formData.bankAccounts || []);
        setProperties(formData.properties || []);
        
        console.log("[EXPENSE_ACCOUNT_EDIT] Successfully loaded expense data");
      } catch (apiError) {
        console.error("[EXPENSE_ACCOUNT_EDIT] API call failed:", apiError);
        console.error("[EXPENSE_ACCOUNT_EDIT] Error details:", {
          message: apiError.message,
          response: apiError.response?.data,
          status: apiError.response?.status,
          stack: apiError.stack
        });
        
        const errorMessage = apiError.response?.data?.message || 
                           apiError.message || 
                           "Failed to load expense data. Please try again.";
        toast.error(errorMessage);
        
        setExpenditure({});
        setTypes([]);
        setBankAccounts([]);
        setProperties([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load expense data");
      setExpenditure({});
      setTypes([]);
      setBankAccounts([]);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load data on component mount
  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [loadData]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setExpenditure(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Handle property search
    if (field === 'property_name') {
      handlePropertySearch(value);
    }
  };

  // Property search function
  const handlePropertySearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setPropertySearchResults([]);
      setShowPropertyDropdown(false);
      return;
    }

    try {
      const response = await fetch(`/api/properties/search?q=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      
      if (data.success) {
        setPropertySearchResults(data.properties || []);
        setShowPropertyDropdown(true);
      }
    } catch (error) {
      console.error('Error searching properties:', error);
      setPropertySearchResults([]);
      setShowPropertyDropdown(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (property) => {
    setExpenditure(prev => ({
      ...prev,
      property_name: property.name,
      property_id: property.id
    }));
    setPropertySearchResults([]);
    setShowPropertyDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyDropdown && !event.target.closest(`.${styles.propertySearchContainer}`)) {
        setShowPropertyDropdown(false);
      }
      if (showDueDatePicker && dueDatePickerRef.current && !dueDatePickerRef.current.contains(event.target)) {
        setShowDueDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyDropdown, showDueDatePicker]);

  // Handle file changes
  const handleFileChange = async (field, file) => {
    if (!file) {
      console.warn(`[${field}] No file provided`);
      return;
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      toast.error(`File size exceeds 100MB limit. Please choose a smaller file.`);
      // Reset file input
      if (field === 'confirmation_file') {
        const input = document.getElementById('confirmation_file_input');
        if (input) input.value = '';
      }
      return;
    }

    console.log(`[${field}] Starting file upload:`, file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);

    try {
      // Upload file to S3
      // Laravel getDirectoryShort() returns: 'public/expenditures/files/'
      // Actual S3 bucket structure: public/expenditures/files/{filename}
      const fileKey = await s3Service.uploadDocument(file, {
        folder: "public/expenditures/files", // Match Laravel S3 bucket structure
        useLaravelStructure: true, // No timestamp prefix
        onProgress: (progress) => {
          console.log(`[${field}] Upload progress:`, progress, '%');
        }
      });

      console.log(`[${field}] File uploaded successfully. S3 key:`, fileKey);

      if (field === 'file') {
        setNewFile(file);
        setExpenditure(prev => ({ ...prev, fileKey }));
      } else if (field === 'confirmation_file') {
        setNewConfirmationFile(file);
        setExpenditure(prev => ({ ...prev, confirmationFileKey: fileKey }));
      }

      toast.success(`${field === 'confirmation_file' ? 'Payment confirmation' : 'Invoice'} uploaded successfully!`);
    } catch (error) {
      console.error(`[${field}] Error uploading file:`, error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to upload ${field === 'confirmation_file' ? 'payment confirmation' : 'invoice'}: ${errorMessage}`);
      
      // Reset file input on error
      if (field === 'confirmation_file') {
        const input = document.getElementById('confirmation_file_input');
        if (input) input.value = '';
        setNewConfirmationFile(null);
        setExpenditure(prev => ({ ...prev, confirmationFileKey: null }));
      }
    }
  };

  // Handle file removal (Laravel route: expenditures.file.remove)
  const handleFileRemove = async (field) => {
    if (!id) {
      toast.error("Cannot remove file: Expenditure ID not found");
      return;
    }

    // Confirm removal
    const fileType = field === 'file' ? 'invoice' : 'payment confirmation';
    if (!window.confirm(`Are you sure you want to remove the ${fileType} file? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);

      // Call backend API to remove file from S3 and database (matching Laravel removeFile)
      const response = await expendituresService.removeFile(id, field);

      if (response && response.success) {
        // Update local state
        if (field === 'file') {
          setExistingFile(null);
          setNewFile(null);
          setExpenditure(prev => ({ 
            ...prev, 
            file: null, 
            fileKey: null 
          }));
          // Reset the file input element
          const fileInputs = document.querySelectorAll('input[type="file"]');
          if (fileInputs.length > 0) {
            fileInputs[0].value = '';
          }
        } else if (field === 'confirmation_file') {
          setExistingConfirmationFile(null);
          setNewConfirmationFile(null);
          setExpenditure(prev => ({ 
            ...prev, 
            confirmation_file: null, 
            confirmationFileKey: null 
          }));
          // Reset the confirmation file input element
          const fileInputs = document.querySelectorAll('input[type="file"]');
          if (fileInputs.length > 1) {
            fileInputs[1].value = '';
          }
        }

        toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} file removed successfully!`);
      } else {
        toast.error(response?.message || `Failed to remove ${fileType} file`);
      }
    } catch (error) {
      console.error(`Error removing ${fileType} file:`, error);
      toast.error(`Failed to remove ${fileType} file. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Download file
  const handleFileDownload = async (field) => {
    try {
      const fileName = field === 'file' ? expenditure.file : expenditure.confirmation_file;
      if (!fileName) {
        toast.error("No file available for download");
        return;
      }
      
      // Use expendituresService to get presigned URL and trigger direct download
      await expendituresService.downloadFile(id, field);
      toast.success("File download started!");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!expenditure.creditor?.trim()) {
      newErrors.creditor = "Company is required";
    }
    if (!expenditure.due_date) {
      newErrors.due_date = "Due date is required";
    }
    if (!expenditure.amount || parseFloat(expenditure.amount) <= 0) {
      newErrors.amount = "Amount is required and must be greater than 0";
    }
    if (!expenditure.invoice_number?.trim()) {
      newErrors.invoice_number = "Invoice number is required";
    }
    if (!expenditure.type_id) {
      newErrors.type_id = "Type is required";
    }
    if (!expenditure.property_id) {
      newErrors.property_id = "Property is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Add all form fields
      Object.keys(expenditure).forEach(key => {
        if (expenditure[key] !== null && expenditure[key] !== undefined) {
          formData.append(key, expenditure[key]);
        }
      });
      
      // Add S3 file keys if available
      if (expenditure.fileKey) {
        formData.append('fileKey', expenditure.fileKey);
        console.log("[EXPENSE_ACCOUNT_EDIT] Sending fileKey:", expenditure.fileKey);
      }
      if (expenditure.confirmationFileKey) {
        formData.append('confirmationFileKey', expenditure.confirmationFileKey);
        console.log("[EXPENSE_ACCOUNT_EDIT] Sending confirmationFileKey:", expenditure.confirmationFileKey);
      } else {
        console.log("[EXPENSE_ACCOUNT_EDIT] No confirmationFileKey to send");
      }
      
      // Add flags for file removal
      if (!existingFile && !newFile) {
        formData.append('remove_file', '1');
      }
      if (!existingConfirmationFile && !newConfirmationFile) {
        formData.append('remove_confirmation_file', '1');
      }
      
      // Call API
      console.log("[EXPENSE_ACCOUNT_EDIT] Submitting form with confirmationFileKey:", expenditure.confirmationFileKey);
      await expendituresService.updateExpenseAccountItem(id, formData);
      toast.success("Expense updated successfully!");
      navigate("/finance/expense-account");
    } catch (error) {
      console.error("Error updating expense:", error);
      toast.error("Failed to update expense");
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/finance/expense-account");
  };

  // Month names array (shared)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

  // Date picker handlers
  const handleDateClick = (field) => {
    if (field === 'due_date') {
      setShowDueDatePicker(!showDueDatePicker);
    }
  };

  const selectDate = (field, day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    setExpenditure(prev => ({ ...prev, [field]: formattedDate }));
    
    if (field === 'due_date') {
      setShowDueDatePicker(false);
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const generateCalendarDays = (month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    
    const days = [];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return {
      days,
      monthName: monthNames[month - 1], 
      year: year,
      month: month
    };
  };

  // Navigation handlers for date picker
  const handlePrevMonth = (field) => {
    if (field === 'due_date') {
      if (dueDatePickerMonth === 1) {
        setDueDatePickerMonth(12);
        setDueDatePickerYear(dueDatePickerYear - 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth - 1);
      }
    }
  };

  const handleNextMonth = (field) => {
    if (field === 'due_date') {
      if (dueDatePickerMonth === 12) {
        setDueDatePickerMonth(1);
        setDueDatePickerYear(dueDatePickerYear + 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth + 1);
      }
    }
  };

  const handleMonthChange = (field, month) => {
    if (field === 'due_date') {
      setDueDatePickerMonth(parseInt(month));
    }
  };

  const handleYearChange = (field, year) => {
    if (field === 'due_date') {
      setDueDatePickerYear(parseInt(year));
    }
  };

  // Generate year options (current year ± 10 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }

  const dueDatePickerCalendar = generateCalendarDays(dueDatePickerMonth, dueDatePickerYear);

  if (loading && !expenditure.id) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading expense data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/finance/expense-account">Finance</Link>
        <span className={styles.separator}>/</span>
        <Link to="/finance/expense-account">Expense Account</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Edit</span>
      </div>

      <div className={styles.mainSection}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panel}>
              <div className={styles.panelHeading}>
                <h3>Edit Expense</h3>
              </div>
              <div className={styles.panelBody}>
                <form onSubmit={handleSubmit}>
                  {/* Property Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Property</label>
                    <div className={styles.colSm5}>
                      <div className={styles.propertySearchContainer}>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={expenditure.property_name || ""}
                          onChange={(e) => handleInputChange('property_name', e.target.value)}
                          placeholder="Search for property..."
                          onFocus={() => {
                            if (propertySearchResults.length > 0) {
                              setShowPropertyDropdown(true);
                            }
                          }}
                        />
                        <input
                          type="hidden"
                          value={expenditure.property_id || ""}
                          onChange={(e) => handleInputChange('property_id', e.target.value)}
                        />
                        {showPropertyDropdown && propertySearchResults.length > 0 && (
                          <div className={styles.propertyDropdown}>
                            {propertySearchResults.map((property) => (
                              <div
                                key={property.id}
                                className={styles.propertyOption}
                                onClick={() => handlePropertySelect(property)}
                              >
                                <div className={styles.propertyName}>{property.name}</div>
                                <div className={styles.propertyDetails}>
                                  {property.property_id} • {property.address_1} • {property.postcode}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.property_name && (
                        <div className={styles.errorMessage}>{errors.property_name}</div>
                      )}
                    </div>
                  </div>

                  {/* Type Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Type</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        required
                        value={expenditure.type_id || ""}
                        onChange={(e) => handleInputChange('type_id', e.target.value)}
                      >
                        <option value="">Please Select</option>
                        {types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.type_id && (
                        <div className={styles.errorMessage}>{errors.type_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Bank Account Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>GG Bank Account</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        value={expenditure.bank_account_id || ""}
                        onChange={(e) => handleInputChange('bank_account_id', e.target.value)}
                      >
                        <option value="">Please Select</option>
                        {bankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                      </select>
                      {errors.bank_account_id && (
                        <div className={styles.errorMessage}>{errors.bank_account_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Company Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Company</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.creditor || ""}
                        onChange={(e) => handleInputChange('creditor', e.target.value)}
                      />
                      {errors.creditor && (
                        <div className={styles.errorMessage}>{errors.creditor}</div>
                      )}
                    </div>
                  </div>

                  {/* Ticket ID Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Ticket Id</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        maxLength={9}
                        value={expenditure.ticket_id || ""}
                        onChange={(e) => handleInputChange('ticket_id', e.target.value)}
                      />
                      {errors.ticket_id && (
                        <div className={styles.errorMessage}>{errors.ticket_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Due Date Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Due Date</label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={dueDatePickerRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          required
                          placeholder="DD/MM/YYYY"
                          value={expenditure.due_date || ""}
                          onChange={(e) => handleInputChange('due_date', e.target.value)}
                          onClick={() => handleDateClick('due_date')}
                          readOnly
                        />
                        {showDueDatePicker && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handlePrevMonth('due_date')}
                                aria-label="Previous month"
                              >
                                ‹
                              </button>
                              <div className={styles.datePickerMonthYear}>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerMonth}
                                  onChange={(e) => handleMonthChange('due_date', e.target.value)}
                                >
                                  {monthNames.map((month, index) => (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerYear}
                                  onChange={(e) => handleYearChange('due_date', e.target.value)}
                                >
                                  {years.map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handleNextMonth('due_date')}
                                aria-label="Next month"
                              >
                                ›
                              </button>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {dueDatePickerCalendar.days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate('due_date', day, dueDatePickerCalendar.month, dueDatePickerCalendar.year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.due_date && (
                        <div className={styles.errorMessage}>{errors.due_date}</div>
                      )}
                    </div>
                  </div>

                  {/* Amount Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Amount</label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.formControl}
                        required
                        value={expenditure.amount || ""}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                      />
                      {errors.amount && (
                        <div className={styles.errorMessage}>{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Invoice Number Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Invoice number</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.invoice_number || ""}
                        onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                      />
                      {errors.invoice_number && (
                        <div className={styles.errorMessage}>{errors.invoice_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Purchase Order Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Purchase Order</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={expenditure.purchase_order_number || ""}
                        onChange={(e) => handleInputChange('purchase_order_number', e.target.value)}
                      />
                      {errors.purchase_order_number && (
                        <div className={styles.errorMessage}>{errors.purchase_order_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Description</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows={3}
                        value={expenditure.description || ""}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                      />
                      {errors.description && (
                        <div className={styles.errorMessage}>{errors.description}</div>
                      )}
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Invoice</label>
                    <div className={styles.colSm5}>
                      {existingFile && !newFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleFileDownload('file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              className={styles.formControl}
                              onChange={(e) => handleFileChange('file', e.target.files[0])}
                            />
                            {newFile && (
                              <button
                                type="button"
                                className={styles.btnDanger}
                                onClick={() => handleFileRemove('file')}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.file && (
                        <div className={styles.errorMessage}>{errors.file}</div>
                      )}
                    </div>
                  </div>

                  {/* Confirmation File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Payment Confirmation</label>
                    <div className={styles.colSm5}>
                      {existingConfirmationFile && !newConfirmationFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingConfirmationFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleFileDownload('confirmation_file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('confirmation_file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              id="confirmation_file_input"
                              className={styles.formControl}
                              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                              disabled={loading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleFileChange('confirmation_file', file);
                                }
                              }}
                            />
                            {newConfirmationFile && (
                              <div className={styles.uploadedFileInfo}>
                                <span>{newConfirmationFile.name}</span>
                                <button
                                  type="button"
                                  className={styles.btnDanger}
                                  onClick={() => {
                                    setNewConfirmationFile(null);
                                    setExpenditure(prev => ({ ...prev, confirmationFileKey: null }));
                                    const input = document.getElementById('confirmation_file_input');
                                    if (input) input.value = '';
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.confirmation_file && (
                        <div className={styles.errorMessage}>{errors.confirmation_file}</div>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className={styles.formGroup} style={{display:'flex', justifyContent:'flex-end'}}>
                    <div className={styles.textRight}>
                      <button
                      style={{background:'#17264B'}}
                        type="submit"
                        className={styles.btnHead}
                        disabled={loading}
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>
                    
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAccountEdit; 